/**
 * Toast Notification Hook
 * Simple toast notification system
 */

import { useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

let toastIdCounter = 0;

const toastListeners: Set<(toasts: Toast[]) => void> = new Set();
let toasts: Toast[] = [];

const notifyListeners = () => {
  toastListeners.forEach((listener) => listener([...toasts]));
};

export const showToast = (message: string, type: ToastType = 'info', duration = 5000) => {
  const id = `toast-${++toastIdCounter}`;
  const toast: Toast = { id, message, type, duration };

  toasts = [...toasts, toast];
  notifyListeners();

  if (duration > 0) {
    setTimeout(() => {
      toasts = toasts.filter((t) => t.id !== id);
      notifyListeners();
    }, duration);
  }
};

export const removeToast = (id: string) => {
  toasts = toasts.filter((t) => t.id !== id);
  notifyListeners();
};

export const useToast = () => {
  const [toastList, setToastList] = useState<Toast[]>(toasts);

  const subscribe = useCallback((callback: (toasts: Toast[]) => void) => {
    toastListeners.add(callback);
    return () => toastListeners.delete(callback);
  }, []);

  useState(() => {
    return subscribe(setToastList);
  });

  return {
    toasts: toastList,
    showToast,
    removeToast,
  };
};
